"use strict";

const HTTP_PROTOCOLS = new Set(["http:", "https:"]);
const HEADER_BUCKETS = [
    "common",
    "delete",
    "get",
    "head",
    "options",
    "post",
    "put",
    "patch",
];
const PUBLIC_SENSITIVE_HEADERS = [
    "Authorization",
    "Proxy-Authorization",
    "Cookie",
    "X-CSRF-TOKEN",
    "X-XSRF-TOKEN",
    "X-Content-Language",
    "X-Softinya",
    "Company",
    "X-Company-Id",
];
const EXTERNAL_AUTH_SENSITIVE_HEADERS = PUBLIC_SENSITIVE_HEADERS.filter(
    (header) => header !== "Authorization"
);
const RESPONSE_ONLY_CORS_HEADERS = [
    "Access-Control-Allow-Origin",
    "Access-Control-Allow-Methods",
    "Access-Control-Allow-Headers",
    "Access-Control-Allow-Credentials",
];

class UrlPolicyError extends Error {
    constructor(message) {
        super(message);
        this.name = "UrlPolicyError";
        this.code = "ERR_UNSAFE_REQUEST_URL";
    }
}

function getUrlValue(value, label) {
    if (value instanceof URL) {
        return value.toString();
    }

    if (typeof value !== "string" || value.trim() === "") {
        throw new UrlPolicyError(`${label} must be a non-empty URL`);
    }

    return value.trim();
}

function assertHttpUrl(url, label) {
    if (!HTTP_PROTOCOLS.has(url.protocol)) {
        throw new UrlPolicyError(`${label} must use HTTP or HTTPS`);
    }

    if (url.username || url.password) {
        throw new UrlPolicyError(`${label} must not contain user information`);
    }

    return url;
}

function parseAbsoluteHttpUrl(value, label) {
    let parsed;

    try {
        parsed = new URL(getUrlValue(value, label));
    } catch (error) {
        if (error instanceof UrlPolicyError) {
            throw error;
        }
        throw new UrlPolicyError(`${label} is malformed`);
    }

    return assertHttpUrl(parsed, label);
}

function normalizeOriginBase(baseUrl, label = "Backend base URL") {
    const parsed = parseAbsoluteHttpUrl(baseUrl, label);
    parsed.pathname = "/";
    parsed.search = "";
    parsed.hash = "";
    return parsed;
}

function resolveExactOriginUrl(requestUrl, trustedBaseUrl, label = "Request URL") {
    const trustedBase = parseAbsoluteHttpUrl(
        trustedBaseUrl,
        "Trusted base URL"
    );
    trustedBase.search = "";
    trustedBase.hash = "";
    trustedBase.pathname = `${trustedBase.pathname.replace(/\/+$/g, "")}/`;
    const trustedOrigin = trustedBase.origin;
    const requestValue = getUrlValue(requestUrl, label);
    let resolved;

    try {
        const hasScheme = /^[a-z][a-z\d+\-.]*:/i.test(requestValue);
        const isProtocolRelative = requestValue.startsWith("//");
        const candidate =
            hasScheme || isProtocolRelative
                ? requestValue
                : `${trustedBase.toString().replace(/\/+$/g, "")}/${requestValue.replace(
                    /^\/+/g,
                    ""
                )}`;
        resolved = new URL(candidate, trustedBase);
    } catch (error) {
        if (error instanceof UrlPolicyError) {
            throw error;
        }
        throw new UrlPolicyError(`${label} is malformed`);
    }

    assertHttpUrl(resolved, label);

    if (resolved.origin !== trustedOrigin) {
        throw new UrlPolicyError(
            `${label} origin ${resolved.origin} is not allowed`
        );
    }

    resolved.hash = "";
    return resolved.toString();
}

function resolveBackendUrl(requestUrl, backendBaseUrl) {
    return resolveExactOriginUrl(requestUrl, backendBaseUrl, "Backend request URL");
}

function resolvePublicUrl(requestUrl) {
    const resolved = parseAbsoluteHttpUrl(requestUrl, "Public request URL");
    resolved.hash = "";
    return resolved.toString();
}

function normalizeEndpointPath(url) {
    let path = url.pathname;

    try {
        path = decodeURIComponent(path);
    } catch (error) {
        // Keep the canonical URL pathname when it contains invalid escapes.
    }

    path = path.replace(/\/+/g, "/");
    if (path.length > 1) {
        path = path.replace(/\/+$/g, "");
    }

    return path.toLowerCase();
}

function isBackendEndpoint(requestUrl, backendBaseUrl, endpointPath) {
    const resolved = new URL(resolveBackendUrl(requestUrl, backendBaseUrl));
    const expected = String(endpointPath || "")
        .replace(/\/+/g, "/")
        .replace(/\/+$/g, "")
        .toLowerCase();
    return normalizeEndpointPath(resolved) === expected;
}

function isNoBearerEndpoint(requestUrl, backendBaseUrl) {
    return (
        isBackendEndpoint(requestUrl, backendBaseUrl, "/api/token") ||
        isBackendEndpoint(requestUrl, backendBaseUrl, "/api/token/refresh")
    );
}

function isAxiosHeaders(headers) {
    return Boolean(
        headers &&
        typeof headers.set === "function" &&
        typeof headers.delete === "function"
    );
}

function cloneHeaders(headers) {
    if (!headers) {
        return {};
    }

    if (isAxiosHeaders(headers)) {
        return headers;
    }

    const cloned = { ...headers };
    HEADER_BUCKETS.forEach((bucket) => {
        if (
            cloned[bucket] &&
            typeof cloned[bucket] === "object" &&
            !Array.isArray(cloned[bucket])
        ) {
            cloned[bucket] = { ...cloned[bucket] };
        }
    });
    return cloned;
}

function deletePlainHeader(headers, headerName) {
    const normalizedName = headerName.toLowerCase();

    Object.keys(headers).forEach((key) => {
        if (key.toLowerCase() === normalizedName) {
            delete headers[key];
        }
    });

    HEADER_BUCKETS.forEach((bucket) => {
        const bucketHeaders = headers[bucket];
        if (
            !bucketHeaders ||
            typeof bucketHeaders !== "object" ||
            Array.isArray(bucketHeaders)
        ) {
            return;
        }

        Object.keys(bucketHeaders).forEach((key) => {
            if (key.toLowerCase() === normalizedName) {
                delete bucketHeaders[key];
            }
        });
    });
}

function deleteHeader(headers, headerName) {
    if (!headers) {
        return;
    }

    if (isAxiosHeaders(headers)) {
        headers.delete(headerName);
        return;
    }

    deletePlainHeader(headers, headerName);
}

function setHeader(headers, headerName, value) {
    deleteHeader(headers, headerName);

    if (isAxiosHeaders(headers)) {
        headers.set(headerName, value);
        return;
    }

    headers[headerName] = value;
}

function getHeader(headers, headerName) {
    if (!headers) {
        return undefined;
    }

    if (typeof headers.get === "function") {
        return headers.get(headerName);
    }

    const normalizedName = headerName.toLowerCase();
    const directKey = Object.keys(headers).find(
        (key) => key.toLowerCase() === normalizedName
    );
    if (directKey) {
        return headers[directKey];
    }

    for (const bucket of HEADER_BUCKETS) {
        const bucketHeaders = headers[bucket];
        if (!bucketHeaders || typeof bucketHeaders !== "object") {
            continue;
        }
        const key = Object.keys(bucketHeaders).find(
            (candidate) => candidate.toLowerCase() === normalizedName
        );
        if (key) {
            return bucketHeaders[key];
        }
    }

    return undefined;
}

function isFormDataPayload(value) {
    if (!value) {
        return false;
    }

    if (typeof FormData !== "undefined" && value instanceof FormData) {
        return true;
    }

    return Object.prototype.toString.call(value) === "[object FormData]";
}

function createPolicyConfig(config) {
    const nextConfig = { ...(config || {}) };
    nextConfig.headers = cloneHeaders(config && config.headers);
    return nextConfig;
}

function applyBackendRequestPolicy(config, options) {
    const {
        backendBaseUrl,
        token,
        language,
        companyId,
        softinyaHeader = "softinyacom",
    } = options || {};
    const nextConfig = createPolicyConfig(config);

    nextConfig.url = resolveBackendUrl(nextConfig.url, backendBaseUrl);
    nextConfig.baseURL = undefined;
    delete nextConfig.auth;

    deleteHeader(nextConfig.headers, "Authorization");
    deleteHeader(nextConfig.headers, "Proxy-Authorization");
    RESPONSE_ONLY_CORS_HEADERS.forEach((header) => {
        deleteHeader(nextConfig.headers, header);
    });

    if (token && !isNoBearerEndpoint(nextConfig.url, backendBaseUrl)) {
        setHeader(nextConfig.headers, "Authorization", `Bearer ${token}`);
    }

    if (language) {
        setHeader(nextConfig.headers, "X-Content-Language", language);
    }
    if (softinyaHeader) {
        setHeader(nextConfig.headers, "X-Softinya", softinyaHeader);
    }
    if (companyId !== null && companyId !== undefined && companyId !== "") {
        setHeader(nextConfig.headers, "Company", String(companyId));
    }

    if (isFormDataPayload(nextConfig.data)) {
        deleteHeader(nextConfig.headers, "Content-Type");
    } else {
        setHeader(nextConfig.headers, "Content-Type", "application/json");
    }

    return nextConfig;
}

function applyPublicRequestPolicy(config) {
    const nextConfig = createPolicyConfig(config);

    nextConfig.url = resolvePublicUrl(nextConfig.url);
    nextConfig.baseURL = undefined;
    nextConfig.withCredentials = false;
    nextConfig.xsrfCookieName = null;
    nextConfig.xsrfHeaderName = null;
    delete nextConfig.auth;

    PUBLIC_SENSITIVE_HEADERS.forEach((header) => {
        deleteHeader(nextConfig.headers, header);
    });
    RESPONSE_ONLY_CORS_HEADERS.forEach((header) => {
        deleteHeader(nextConfig.headers, header);
    });

    if (isFormDataPayload(nextConfig.data)) {
        deleteHeader(nextConfig.headers, "Content-Type");
    }

    return nextConfig;
}

function applyIsolatedExternalAuthPolicy(config, allowedBaseUrl) {
    const nextConfig = createPolicyConfig(config);

    nextConfig.url = resolveExactOriginUrl(
        nextConfig.url,
        allowedBaseUrl,
        "External authenticated request URL"
    );
    nextConfig.baseURL = undefined;
    nextConfig.withCredentials = false;
    nextConfig.xsrfCookieName = null;
    nextConfig.xsrfHeaderName = null;
    delete nextConfig.auth;

    EXTERNAL_AUTH_SENSITIVE_HEADERS.forEach((header) => {
        deleteHeader(nextConfig.headers, header);
    });
    RESPONSE_ONLY_CORS_HEADERS.forEach((header) => {
        deleteHeader(nextConfig.headers, header);
    });

    if (isFormDataPayload(nextConfig.data)) {
        deleteHeader(nextConfig.headers, "Content-Type");
    }

    return nextConfig;
}

function createNativeDownloadRequest(requestUrl, backendBaseUrl, token) {
    try {
        const url = resolveBackendUrl(requestUrl, backendBaseUrl);
        return {
            url,
            headers: token
                ? {
                      Authorization: `Bearer ${token}`,
                  }
                : {},
        };
    } catch (backendError) {
        return {
            url: resolvePublicUrl(requestUrl),
            headers: {},
        };
    }
}

module.exports = {
    UrlPolicyError,
    applyBackendRequestPolicy,
    applyIsolatedExternalAuthPolicy,
    applyPublicRequestPolicy,
    createNativeDownloadRequest,
    deleteHeader,
    getHeader,
    isBackendEndpoint,
    isFormDataPayload,
    isNoBearerEndpoint,
    normalizeOriginBase,
    resolveBackendUrl,
    resolveExactOriginUrl,
    resolvePublicUrl,
    setHeader,
};
